import { Check, Pause, Play, RotateCcw, SkipForward, Square } from 'lucide-react';
import type { ComponentType, CSSProperties, ReactNode } from 'react';
import type { Project } from '@timato/shared';
import { Kbd, Tooltip } from '@/components/ui';
import { cn } from '@/lib/cn';
import { timerButtonVars } from '@/lib/projectColors';
import type { TimerApi } from '@/timer/useTimer';

const Hint = ({ text, keycap }: { text: string; keycap: string }) => <>{text}<Kbd className="bg-transparent border-toast-muted text-toast-muted">{keycap}</Kbd></>;

/** 48px circle, hairline border, card ground (4a/8a). */
function CircleButton({ icon: Icon, label, onClick, disabled }: { icon: ComponentType<{ size?: number }>; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label}
      className="w-12 h-12 rounded-full border border-chip-border bg-card text-ink grid place-items-center cursor-pointer transition-colors hover:bg-inset disabled:opacity-40 disabled:cursor-not-allowed">
      <Icon size={17} />
    </button>
  );
}

/** 56px pill, 28px radius, 15px extrabold - the only primary action on the screen. */
export function PillButton({ children, onClick, tone = 'ink', className, style }: {
  children: ReactNode; onClick: () => void; tone?: 'ink' | 'muted' | 'timer'; className?: string; style?: CSSProperties;
}) {
  return (
    <button type="button" onClick={onClick} style={style}
      className={cn('h-14 min-w-[150px] px-7 rounded-[28px] border-0 font-extrabold text-[15px] cursor-pointer inline-flex items-center justify-center gap-[9px] transition-[background-color,color,opacity] duration-300 hover:opacity-90',
        tone === 'ink' && 'bg-primary-btn-bg text-primary-btn-fg',
        tone === 'muted' && 'bg-selected text-ink',
        // 3a: ink on the light page, the project's own colour on the dark one, where an ink button would be
        // a white slab. The caller always sets both variables - a `var()` fallback inside an arbitrary value
        // has a comma in it, which Tailwind will not parse, and the class is silently never generated.
        tone === 'timer' && 'bg-primary-btn-bg text-primary-btn-fg dark:bg-[var(--timer-btn)] dark:text-[var(--timer-btn-fg)]',
        className)}>
      {children}
    </button>
  );
}

/**
 * Countdown: Reset ○ · Start/Pause pill · Skip ○. Track: Discard ○ · Pause · Finish (8a). The primary pill takes the
 * project's colour in both modes, as the ring does, so Track reads as the same session as Focus.
 */
export function TimerControls({ api, project }: { api: TimerApi; project?: Project }) {
  const { timer, isCountdown, actions } = api;
  const projectTone = timerButtonVars(project);
  const idle = timer.status === 'idle';
  const running = timer.status === 'running';
  const label = running ? 'Pause' : idle ? 'Start' : 'Resume';
  const Icon = running ? Pause : Play;

  if (!isCountdown) {
    return (
      <div className="flex items-center gap-3">
        {!idle && <Tooltip label="Discard this run"><span><CircleButton icon={RotateCcw} label="Discard" onClick={() => void actions.reset()} /></span></Tooltip>}
        <Tooltip label={<Hint text={`${label} timer`} keycap="Space" />}>
          <PillButton onClick={() => void actions.toggle()} tone={idle ? 'timer' : 'muted'} style={projectTone}><Icon size={16} strokeWidth={2.5} />{label}</PillButton>
        </Tooltip>
        {!idle && (
          <Tooltip label={<Hint text="Finish and log this run" keycap="S" />}>
            <PillButton onClick={() => void actions.finish()} tone="timer" style={projectTone}><Square size={12} fill="currentColor" strokeWidth={0} />Finish</PillButton>
          </Tooltip>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <Tooltip label={<Hint text="Reset" keycap="R" />}><span><CircleButton icon={RotateCcw} label="Reset" onClick={() => void actions.reset()} disabled={idle} /></span></Tooltip>
      <Tooltip label={<Hint text={`${label} timer`} keycap="Space" />}>
        <PillButton onClick={() => void actions.toggle()} tone="timer" style={projectTone} className="min-w-[190px]"><Icon size={16} strokeWidth={2.5} />{label}</PillButton>
      </Tooltip>
      {/* Not in a preview (TW-33): there it would skip the run going on under it, not the mode on screen. */}
      <Tooltip label={<Hint text="Skip to next" keycap="S" />}><span><CircleButton icon={idle ? SkipForward : Check} label="Skip" onClick={() => void actions.skip()} disabled={api.previewing} /></span></Tooltip>
    </div>
  );
}
